import { apiClient } from './client';
import type { AuthUser } from '@/stores/auth.store';

export interface LoginPayload {
  username: string;
  password: string;
  tenantSlug?: string;
}

export interface LoginResponse {
  user: AuthUser;
  token: string;
  refreshToken: string;
  tenantId: string;
  tenantSlug: string;
}

// Shape that NestJS backend actually returns
interface BackendAuthSession {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    role: 'master' | 'admin' | 'operator' | 'viewer';
    tenantId: string;
    tenantSlug: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    tokenType: 'Bearer';
    expiresIn: number;
  };
}

function adaptSession(raw: BackendAuthSession): LoginResponse {
  return {
    user: {
      id: raw.user.id,
      username: raw.user.username,
      display_name: raw.user.displayName ?? undefined,
      role: raw.user.role,
      permissions: [],
    },
    token: raw.tokens.accessToken,
    refreshToken: raw.tokens.refreshToken,
    tenantId: raw.user.tenantId,
    tenantSlug: raw.user.tenantSlug,
  };
}

export const authApi = {
  login: async (payload: LoginPayload): Promise<LoginResponse> => {
    const { data } = await apiClient.post<BackendAuthSession>('/auth/login', payload);
    return adaptSession(data);
  },

  refresh: async (refreshToken: string): Promise<LoginResponse> => {
    const { data } = await apiClient.post<BackendAuthSession>('/auth/refresh', { refreshToken });
    return adaptSession(data);
  },

  logout: async (refreshToken?: string): Promise<void> => {
    await apiClient.post('/auth/logout', { refreshToken: refreshToken ?? '' }).catch(() => undefined);
  },

  me: async (): Promise<AuthUser> => {
    const { data } = await apiClient.get<AuthUser>('/auth/me');
    return data;
  },
};
